import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  // Slider,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import {
  COLOR_BACKGROUND_WARM,
  COLOR_BORDER,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_WHITE,
} from '../styles/colors';
import { PlayerEvaluationSchema, ProgramEvaluationSchema, PlayerEvaluationDataResponse } from '../types/Evaluation';
import {
  buildDefaultValues,
  transformApiResponseToFormValues,
  transformFormDataToPlayerEvaluationData,
} from '../services/evaluation-helpers';
import { savePlayerProfile } from '../services/evaluation-api';
import TabsRenderer from './evaluation/TabsRenderer';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Slider from '../atoms/Slider';
import Player from '../types/Player';
import Toast, { ToastType } from '../atoms/Toast';
import Team from '../types/Team';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 620,
    width: '100%',
    height: '100%',
    backgroundColor: COLOR_WHITE,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
  },
  evaluationReportDrawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
  },
  evaluationReportDrawerHeaderTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 600,
  },
  evaluationReportDrawerHeaderCloseButton: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: COLOR_ORANGE,
    '&:hover': {
      opacity: 0.8,
    },
  },
  tabsRoot: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
  },
  tabsIndicator: {
    height: 3,
    backgroundColor: COLOR_ORANGE,
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 0,
    padding: theme.spacing(1.5, 3),
    fontSize: theme.typography.pxToRem(14),
  },
  tabSelected: {
    fontWeight: 700,
  },
  confidentialBar: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
    backgroundColor: COLOR_BACKGROUND_WARM,
    fontSize: theme.typography.pxToRem(12),
  },
  scoreCard: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: COLOR_BACKGROUND_WARM,
    border: `1px solid ${COLOR_BORDER}`,
  },
  scoreCardRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  metricBox: {
    flex: '0 0 140px',
    minWidth: 140,
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: COLOR_WHITE,
    border: `1px solid ${COLOR_BORDER}`,
  },
  metricLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  evalColorLabel: {
    fontSize: theme.typography.pxToRem(16),
  },
  metricValue: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
  },
  metricValueInput: {
    width: '100%',
    border: 0,
    outline: 'none',
    background: 'transparent',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'inherit',
    fontFamily: 'inherit',
    padding: 0,
    margin: 0,
  },
  colorSwatchWrapper: {
    flex: '0 0 155px',
    minWidth: 120,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  colorSwatchLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: COLOR_MIDDLE_GRAY,
    marginBottom: theme.spacing(1),
  },
  colorSwatch: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: '#ee5a46',
    border: `1px solid ${COLOR_BORDER}`,
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  colorSwatchInput: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    border: 0,
    padding: 0,
    cursor: 'pointer',
  },
  sliderRow: {
    marginTop: theme.spacing(2),
  },
  sliderHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
  },
  sliderInfoIcon: {
    marginLeft: theme.spacing(0.5),
    fontSize: 16,
    color: COLOR_MIDDLE_GRAY,
  },
  sliderLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.primary,
  },
  rail: {
    height: 10,
  },
  thumb: {
    height: 20,
    width: 20,
  },
  shimmerContainer: {
    padding: theme.spacing(2),
  },
  shimmerTabs: {
    height: 40,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(90deg, #f1f1f1 25%, #e6e6e6 37%, #f1f1f1 63%)',
    backgroundSize: '400% 100%',
    animation: '$shimmer 1.4s ease infinite',
  },
  shimmerCard: {
    height: 120,
    borderRadius: 10,
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(90deg, #f1f1f1 25%, #e6e6e6 37%, #f1f1f1 63%)',
    backgroundSize: '400% 100%',
    animation: '$shimmer 1.4s ease infinite',
  },
  shimmerRow: {
    height: 46,
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    background: 'linear-gradient(90deg, #f1f1f1 25%, #e6e6e6 37%, #f1f1f1 63%)',
    backgroundSize: '400% 100%',
    animation: '$shimmer 1.4s ease infinite',
  },
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '100% 0',
    },
    '100%': {
      backgroundPosition: '0 0',
    },
  },
}), { name: 'EvaluationDrawerContent' });

/**
 * Score card that shows read-only player metrics from the API schema.
 */
function ScoreCard({
  schema,
  classes,
  control,
}: {
  schema: PlayerEvaluationSchema;
  classes: ReturnType<typeof useStyles>;
  control: any;
}) {
  return (
    <>
      <Box className={classes.confidentialBar}>
        <Typography variant="body2">
          <strong>Confidential Evaluation:</strong> Only viewable by your program
        </Typography>
      </Box>

      <Box className={classes.scoreCard}>
        <Box className={classes.scoreCardRow}>
          <Box className={classes.metricBox}>
            <div className={classes.metricLabel}>Player Evaluation Score</div>
            <div className={classes.metricValue}>
              <Controller
                name="evalScore"
                control={control}
                defaultValue={schema.playerEvaluationScore}
                render={({ field }) => (
                  <input
                    type="number"
                    className={classes.metricValueInput}
                    value={field.value ?? ''}
                    onChange={event => field.onChange(event.target.value)}
                  />
                )}
              />
            </div>
          </Box>
          <Box className={classes.metricBox}>
            <div className={classes.metricLabel}>Player Evaluation Code</div>
            <div className={classes.metricValue}>
              <Controller
                name="evalCode"
                control={control}
                defaultValue={schema.playerEvaluationCode}
                render={({ field }) => (
                  <input
                    type="text"
                    className={classes.metricValueInput}
                    value={field.value ?? ''}
                    onChange={event => field.onChange(event.target.value)}
                  />
                )}
              />
            </div>
          </Box>
          <Box className={classes.colorSwatchWrapper}>
            <div className={classes.evalColorLabel}>Evaluation Color:</div>
            <Controller
              name="evalColor"
              control={control}
              defaultValue={schema.evaluationColor}
              render={({ field }) => (
                <div className={classes.colorSwatch} style={{ backgroundColor: field.value || schema.evaluationColor }}>
                  <input
                    type="color"
                    className={classes.colorSwatchInput}
                    value={field.value || '#000000'}
                    onChange={event => field.onChange(event.target.value)}
                  />
                </div>
              )}
            />
          </Box>
        </Box>
        <Box className={classes.sliderRow}>
          <div className={classes.sliderHeader}>
            <span>Player Display:</span>
            <InfoOutlinedIcon className={classes.sliderInfoIcon} />
          </div>
          <Slider
            defaultValue={0}
            step={1}
            marks={[{ value: 0 }, { value: 1 }, { value: 2 }, { value: 3 }]}
            min={0}
            max={3}
            onChange={() => console.log('clicked slider')}
            // className={clsx(classes.rail)}
          />
          <div className={classes.sliderLabels}>
            <span>All</span>
            <span>Skip</span>
            <span>Only this</span>
            <span>None</span>
          </div>
        </Box>
      </Box>
    </>
  );
}

interface EvaluationDrawerContentProps {
  programEvaluationDataSchema?: ProgramEvaluationSchema;
  playerEvaluationData?: PlayerEvaluationDataResponse | null;
  isLoading?: boolean;
  player?: Player;
  onClose: () => void;
  evaluationTeam: Team | null | undefined;
  onRefetchPlayerEvaluationData?: () => Promise<any>;
}

export default function EvaluationDrawerContent(props: EvaluationDrawerContentProps) {
  const {
    programEvaluationDataSchema,
    playerEvaluationData,
    isLoading = false,
    player,
    onClose,
    evaluationTeam,
    onRefetchPlayerEvaluationData,
  } = props;
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.INFO);
  const [toastMessage, setToastMessage] = useState('');

  // In production, replace with data from API calls.
  const schema: PlayerEvaluationSchema | undefined = useMemo(
    () => transformApiResponseToFormValues(programEvaluationDataSchema || null, playerEvaluationData || null),
    [programEvaluationDataSchema, playerEvaluationData],
  );

  const defaultValues = useMemo(
    () => (schema
      ? {
        ...buildDefaultValues(schema),
        evalScore: schema.playerEvaluationScore,
        evalCode: schema.playerEvaluationCode,
        evalColor: schema.evaluationColor,
      }
      : {}),
    [schema],
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSave = useCallback(
    async (formData: Record<string, any>) => {
      if (!schema) return;
      if (!player?.id) return;
      setIsSaving(true);
      try {
        const payload = transformFormDataToPlayerEvaluationData(
          formData,
          schema,
          playerEvaluationData,
        );
        payload.customFields.evalScore = { value: formData.evalScore };
        payload.customFields.evalCode = { value: formData.evalCode };
        payload.customFields.evalColor = { value: formData.evalColor };

        const result = await savePlayerProfile(payload, player.id);
        if (result.success) {
          if (onRefetchPlayerEvaluationData) {
            await onRefetchPlayerEvaluationData();
          }
          console.log('Player profile saved successfully');
          setToastMessage('Evaluation saved successfully');
          setToastType(ToastType.SUCCESS);
          setToastVisible(true);
        } else {
          setToastMessage('Failed to save evaluation');
          setToastType(ToastType.ERROR);
          setToastVisible(true);
        }
      } catch (error) {
        console.error('Failed to save player profile:', error);
        setToastMessage('Failed to save evaluation');
        setToastType(ToastType.ERROR);
        setToastVisible(true);
      } finally {
        setIsSaving(false);
      }
    },
    [schema, playerEvaluationData, player?.id, onRefetchPlayerEvaluationData],
  );

  if (!schema) {
    return null;
  }

  const showShimmer = Boolean(programEvaluationDataSchema && (isLoading || !playerEvaluationData));

  return (
    <>
      <form
        className={classes.root}
        onSubmit={handleSubmit(onSave)}
        noValidate
      >
        {/* Header */}
        <Box className={classes.evaluationReportDrawerHeader}>
          <Typography className={classes.evaluationReportDrawerHeaderTitle}>
            <img
              src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
              alt="Auburn Tigers"
              width={20}
              height={20}
            />
            <span style={{marginLeft: '8px'}}>
              Evaluation
            </span>
          </Typography>
          <Box display="flex" alignItems="center">
            <Box
              mr={1}
              className={classes.saveButton}
              onClick={handleSubmit(onSave)}
            >
              {isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon fontSize="small" />
              )}
              <Box ml={0.5} fontSize={12}>
                {isSaving ? 'Saving...' : 'Save All'}
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon className={classes.evaluationReportDrawerHeaderCloseButton} />
            </IconButton>
          </Box>
        </Box>

        {showShimmer ? (
          <div className={classes.shimmerContainer}>
            <div className={classes.shimmerTabs} />
            <div className={classes.shimmerCard} />
            <div className={classes.shimmerRow} />
            <div className={classes.shimmerRow} />
            <div className={classes.shimmerRow} />
          </div>
        ) : (
          <>
        {/* Tabs — dynamically rendered from schema */}
        <Tabs
          className={classes.tabsRoot}
          value={selectedTab}
          classes={{ indicator: classes.tabsIndicator }}
          onChange={(_event: any, value: number) => setSelectedTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {schema.customTabs.map((tab, idx) => (
            <Tab
              key={tab.name}
              label={tab.name}
              value={idx}
              classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
            />
          ))}
        </Tabs>

        {/* Scrollable tab content */}
        <div className={classes.scrollableContent}>
          {/* Score card shown on first tab */}
          {selectedTab === 0 && <ScoreCard schema={schema} classes={classes} control={control} />}

          {/* Dynamic fields for the selected tab */}
          {schema.customTabs.map((tab, tabIdx) => (
            <div
              key={tab.name}
              role="tabpanel"
              hidden={selectedTab !== tabIdx}
            >
              {selectedTab === tabIdx && (
                <TabsRenderer
                  tab={tab}
                  tabIndex={tabIdx}
                  control={control}
                />
              )}
            </div>
          ))}
        </div>
          </>
        )}
      </form>
      <Toast
        visible={toastVisible}
        type={toastType}
        onHide={() => setToastVisible(false)}
      >
        {toastMessage}
      </Toast>
    </>
    
  );
}